import {
    Alert,
    Button,
    Col,
    Divider,
    Form,
    Input,
    Modal,
    Spin,
    notification,
    Row,
    Space,
} from 'antd';
import AppBorderedPanel from '../../ccx/common/AppBorderedPanel';
import { CheckCircleOutlined } from '@severalnines/frontend_hub/libs/icons';
import { Fragment, useState } from 'react';
import styles from './ChangePasswordButton.module.less';
import AuthService from '../../../services/AuthService';

interface ChangePasswordButtonProps {
    readonly testId?: string;
    readonly onCancel?: Function;
    readonly onChangePasswordSuccess?: Function;
}

type PasswordValidation = {
    validate: boolean;
    label: string;
};

function ChangePasswordModal({
    onCancel,
    onChangePasswordSuccess,
    testId = 'ChangePasswordModal',
}: ChangePasswordButtonProps) {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [passwordValidations, setPasswordValidations] = useState<
        PasswordValidation[]
    >([]);
    const [showPasswordRules, setShowPasswordRules] = useState<boolean>(false);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const validatePasswordStrength = (password: string) => {
        const validationRules: PasswordValidation[] = [
            {
                validate: password.length >= 8,
                label: '8 Letters',
            },
            {
                validate: /[!@#$%^&*()\-_=+{};:,<.>]/.test(password),
                label: '1 special character',
            },
            {
                validate: /[A-Z]/.test(password),
                label: '1 uppercase letter',
            },
            {
                validate: /\d/.test(password),
                label: '1 number',
            },
            { validate: /[a-z]/.test(password), label: '1 lowercase letter' },
        ];

        setPasswordValidations(validationRules);
    };

    const isPasswordValid = (password: string): boolean => {
        const validationRules = [
            password.length >= 8,
            /[!@#$%^&*()\-_=+{};:,<.>]/.test(password),
            /[A-Z]/.test(password),
            /\d/.test(password),
            /[a-z]/.test(password),
        ];
        return validationRules.every((rule) => rule);
    };

    const onSubmit = async () => {
        setIsLoading(true);
        try {
            if (showPasswordRules) return;

            const formValid = await form.validateFields();
            await AuthService.updatePassword(formValid);
            form.resetFields();
            onChangePasswordSuccess?.();
            setIsVisible(false);
            notification.success({
                message: 'Password updated successfully',
            });
        } catch (error: any) {
            notification.error({
                message: 'Error updating password',
                description: error.message,
            });
            console.log('error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setIsVisible(false);
        onCancel?.();
    };

    return (
        <Fragment>
            <Button onClick={() => setIsVisible(true)}>Change Password</Button>
            <Modal
                title={<strong>{`Change password`}</strong>}
                open={isVisible}
                width={500}
                data-testid={testId}
                footer={null}
                cancelText="Cancel"
                onCancel={handleCancel}
                destroyOnClose={true}
            >
                <Spin spinning={isLoading}>
                    <Alert
                        showIcon
                        message="We recommend you to change your password every three months."
                    />
                    <br />
                    <Form
                        layout="vertical"
                        form={form}
                        initialValues={{}}
                        onFinish={onSubmit}
                    >
                        <Row gutter={24}>
                            <Col span={24}>
                                <Form.Item noStyle>
                                    <Form.Item
                                        name="oldPassword"
                                        label="Current Password"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please enter current password',
                                            },
                                        ]}
                                    >
                                        <Input.Password
                                            autoComplete="off"
                                            data-testid={`${testId}OldPasswordInput`}
                                            placeholder="Type your current password"
                                            size="large"
                                        />
                                    </Form.Item>
                                </Form.Item>
                            </Col>
                            <Divider style={{ margin: '16px 0' }} />
                            <Col span={24}>
                                <Form.Item noStyle>
                                    <Form.Item
                                        name="newPassword"
                                        label="New Password"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please enter a new password',
                                            },
                                            ({ getFieldValue }: any) => ({
                                                validator(
                                                    _: any,
                                                    value: string
                                                ) {
                                                    if (!value) {
                                                        return Promise.resolve();
                                                    }

                                                    const oldPassword =
                                                        getFieldValue(
                                                            'oldPassword'
                                                        );
                                                    if (value === oldPassword) {
                                                        return Promise.reject(
                                                            new Error(
                                                                'New password must be different from current password'
                                                            )
                                                        );
                                                    }

                                                    if (
                                                        !isPasswordValid(value)
                                                    ) {
                                                        return Promise.reject(
                                                            new Error(
                                                                'Password does not meet security requirements'
                                                            )
                                                        );
                                                    }

                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password
                                            autoComplete="off"
                                            data-testid={`${testId}NewPasswordInput`}
                                            placeholder="Type your new password"
                                            size="large"
                                            onFocus={() =>
                                                setShowPasswordRules(true)
                                            }
                                            onChange={(e) =>
                                                validatePasswordStrength(
                                                    e.target.value
                                                )
                                            }
                                            onBlur={() => {
                                                const isFalse =
                                                    passwordValidations?.find(
                                                        (
                                                            rule: PasswordValidation
                                                        ) => !rule.validate
                                                    );
                                                if (!isFalse) {
                                                    setShowPasswordRules(false);
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Form.Item>
                                {showPasswordRules &&
                                    !!passwordValidations.length && (
                                        <AppBorderedPanel>
                                            <Row align={'middle'}>
                                                A valid password must contain at
                                                least
                                                {passwordValidations?.map(
                                                    (
                                                        rule: PasswordValidation,
                                                        index: number
                                                    ) => (
                                                        <Col
                                                            key={`${rule.label}-${index}`}
                                                            sm={12}
                                                            xs={24}
                                                            className={
                                                                rule.validate
                                                                    ? styles.ValidationSuccess
                                                                    : styles.ValidationFailure
                                                            }
                                                        >
                                                            <CheckCircleOutlined
                                                                className={
                                                                    rule.validate
                                                                        ? styles.ValidationSuccess
                                                                        : styles.ValidationFailure
                                                                }
                                                            />{' '}
                                                            {rule.label}
                                                        </Col>
                                                    )
                                                )}{' '}
                                            </Row>
                                        </AppBorderedPanel>
                                    )}
                            </Col>
                            <Col span={24} style={{ marginTop: 16 }}>
                                <Form.Item noStyle>
                                    <Form.Item
                                        dependencies={['newPassword']}
                                        name="confirmPassword"
                                        label="Repeat password"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please confirm your password',
                                            },
                                            ({ getFieldValue }: any) => ({
                                                validator(
                                                    _: any,
                                                    value: string
                                                ) {
                                                    if (
                                                        !value ||
                                                        getFieldValue(
                                                            'newPassword'
                                                        ) === value
                                                    ) {
                                                        return Promise.resolve();
                                                    } else {
                                                        return Promise.reject(
                                                            new Error(
                                                                '"New password" and "Repeat password" don\'t match'
                                                            )
                                                        );
                                                    }
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password
                                            autoComplete="off"
                                            data-testid={`${testId}ConfirmPasswordInput`}
                                            placeholder="Enter your password again"
                                            size="large"
                                        />
                                    </Form.Item>
                                </Form.Item>
                                <Divider />
                                <Space
                                    style={{
                                        width: '100%',
                                        justifyContent: 'end',
                                    }}
                                    size="middle"
                                >
                                    <Button onClick={handleCancel}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={isLoading}
                                    >
                                        Change
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Form>
                </Spin>
            </Modal>
        </Fragment>
    );
}

export default ChangePasswordModal;
